import { useMemo, useRef, useState } from 'react';
import { useTelechargementPdf } from '../lib/impression.tsx';
import { demandeV1 } from '../lib/impression.ts';
import { progressionsV1, titreV1 } from '../lib/gabarits-v1.ts';
import { useSearchParams } from 'react-router';
import {
  ActionsDialogue, BarreOutils, Bouton, Carte, classeListeDialogue, Dialogue, Etiquette, GroupeBoutons,
  IconeTelecharger, Message, Selecteur, SqueletteTableau, Vide, ZoneTexte,
  EnTetePage, couleurDonnees, paveTeinte, useRetours, useTheme,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import { useAnneeActive } from '../lib/annee.tsx';
import {
  lireSemainesPerso, semainesDePeriode, type Matiere, type Periode,
} from './Reglages.tsx';
import { decalerJour } from './Journal.tsx';
import { sansPrefixeNiveau } from '../components/SelecteurContenu.tsx';
import {
  TableauProgressions, bullesReportees, enSousBulles, seanceToutReportee,
  type ColonneSousDomaine, type LigneSemaine, type SequenceCellule,
} from '../components/TableauProgressions.tsx';

/**
 * Progression de la période.
 *
 * ── L'écran de l'application d'origine, repris tel quel ─────────────────────
 * Onglets de période en haut, choix de la matière, puis un tableau : les
 * SEMAINES en lignes (S1, S2…), les sous-domaines en colonnes. On y prépare une
 * période entière, pas une semaine — c'est la différence entre une progression
 * et un emploi du temps.
 *
 * ── Ce que l'enseignant appelle « sous-domaine » ────────────────────────────
 * Le référentiel officiel a trois niveaux : matière → domaine → sous-domaine.
 * « Lecture », « Écriture », « Oral » y sont des DOMAINES ; leurs sous-domaines
 * sont les notions détaillées. L'enseignant, lui, appelle sous-domaines ces
 * domaines-là, et c'est eux qu'il veut en colonnes — comme dans la
 * programmation, dont cet écran puise les séances.
 *
 * ── Le report se fait à la croix, pas dans une boîte de dialogue ────────────
 * Vendredi soir, on n'a pas traité la moitié d'une séance. Une croix sur le
 * titre repousse toute la séance à la semaine suivante ; une croix sur un
 * détail ne repousse que lui, avec le titre. Recliquer annule. C'est le geste
 * le plus fréquent de l'écran : il ne doit pas coûter l'ouverture d'un
 * formulaire.
 */

export interface Progression {
  id: string;
  termId: string | null;
  weekStart: string;
  subjectId: string;
  subjectAreaId: string | null;
  /** DOMAINE du référentiel : la colonne du tableau. */
  curriculumDomainId: string | null;
  curriculumSubdomainId: string | null;
  sequence: string;
  content: string;
  competencies: string;
  done: boolean;
  position: number;
  /** Jour visé, 1 = lundi … 7 = dimanche. NULL = pas de jour précis. */
  day: number | null;
  reportedFromId: string | null;
}

interface BlocProgrammation {
  id: string;
  subjectId: string;
  curriculumDomainId: string | null;
  termId: string;
  content: string;
  source: 'libre' | 'programme';
}

/**
 * Jours ouvrés de la semaine.
 *
 * Numérotation ISO, comme l'emploi du temps et la base : 1 = lundi. La
 * tentation de repasser en index de tableau décalerait tout d'un jour sans
 * qu'aucune validation ne bronche, la valeur restant dans l'intervalle permis.
 */
export const JOURS_SEMAINE = [
  { numero: 1, nom: 'Lundi', abrege: 'Lun' },
  { numero: 2, nom: 'Mardi', abrege: 'Mar' },
  { numero: 3, nom: 'Mercredi', abrege: 'Mer' },
  { numero: 4, nom: 'Jeudi', abrege: 'Jeu' },
  { numero: 5, nom: 'Vendredi', abrege: 'Ven' },
] as const;

/** Lundi de la semaine contenant cette date, en ISO. */
export function lundiDe(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  // `getUTCDay()` : 0 = dimanche. Le dimanche appartient à la semaine qui
  // s'achève, il faut donc reculer de six jours et non d'un seul.
  const jour = date.getUTCDay();
  const recul = jour === 0 ? 6 : jour - 1;
  return decalerJour(iso, -recul);
}

/** Découpe le champ multi-ligne en sous-bulles, à l'identique du serveur. */
export function enLignes(texte: string): string[] {
  return texte === '' ? [] : texte.split('\n');
}

/**
 * Une semaine tombe-t-elle dans des vacances ?
 *
 * On compare du lundi au VENDREDI, pas au dimanche : une période qui reprend le
 * lundi suivant n'a pas à voir sa dernière semaine grisée pour un week-end de
 * congés. C'est la règle de l'application d'origine.
 */
export function semaineEnVacances(
  lundi: string,
  vacances: Array<{ debut: string; fin: string }>,
): boolean {
  const vendredi = decalerJour(lundi, 4);
  return vacances.some((v) => lundi <= v.fin && vendredi >= v.debut);
}

export function Progressions() {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();
  const annee = useAnneeActive();
  const [parametres, setParametres] = useSearchParams();

  /*
   * Les matières de la CLASSE, pas celles de l'école.
   *
   * Cet écran se met en face de la programmation, qui lit le programme
   * officiel filtré par les niveaux de la classe. Sans `scope=classroom` il
   * lisait la table brute des matières — d'où deux listes différentes sur un
   * même travail, et une matière programmée qu'on ne retrouvait pas ici.
   */
  const matieres = useDonnees<Matiere[]>('/subjects?scope=classroom');
  const periodes = useDonnees<Periode[]>('/terms');
  const reglages = useDonnees<Record<string, string>>('/settings');
  const { executer, enCours, erreur: erreurEcriture } = useEcriture();



  const [resume, setResume] = useState<string | null>(null);
  const [rattrapage, setRattrapage] = useState(false);

  /*
   * Période affichée : celle de l'adresse, sinon celle d'aujourd'hui, sinon la
   * première. Passer par l'adresse permet de revenir sur la même période après
   * un aller-retour dans le cahier journal.
   */
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const periodeCourante = periodes.donnees?.find(
    (p) => p.startsOn <= aujourdhui && aujourdhui <= p.endsOn,
  );
  const periodeId = parametres.get('periode')
    ?? periodeCourante?.id
    ?? periodes.donnees?.[0]?.id
    ?? '';
  const periode = periodes.donnees?.find((p) => p.id === periodeId);

  const [matiereFiltre, setMatiereFiltre] = useState('');
  const matiereActive = matieres.donnees?.find((m) => m.id === matiereFiltre)
    ?? matieres.donnees?.[0];

  /** La période ENTIÈRE : le tableau la montre d'un seul tenant. */
  const progressions = useDonnees<Progression[]>(
    periodeId ? `/progressions?termId=${periodeId}` : null, [periodeId],
  );

  /**
   * Copies reportées par-dessus les vacances, parties dans la période suivante.
   * Absentes de la liste ci-dessus, elles seules disent que la croix d'une
   * séance de fin de période est déjà rouge.
   */
  const reporteesAilleurs = useDonnees<Progression[]>(
    periodeId ? `/progressions/reported-elsewhere?termId=${periodeId}` : null, [periodeId],
  );

  /** Blocs de la programmation : ils alimentent le choix « + Séance ». */
  const blocs = useDonnees<BlocProgrammation[]>(
    periodeId ? `/plan-blocks?termId=${periodeId}` : null, [periodeId],
  );

  /**
   * Référentiel : il fournit les domaines qui font les colonnes.
   *
   * Le programme de la CLASSE, toutes versions fusionnées — une classe à
   * cheval sur deux cycles perdait sinon la moitié de ses colonnes.
   */
  const arbreProg = useDonnees<Array<{
    id: string; name: string; subject: string; color: string;
    subdomains: Array<{ id: string; name: string }>;
  }>>('/curriculum/tree');

  /*
   * Calendrier officiel des vacances : il grise les semaines chômées.
   *
   * Sans lui, une semaine de congés se présente comme une semaine ordinaire —
   * et l'on y planifie une séance qui n'aura jamais lieu.
   */
  const anneeRentree = useMemo(() => {
    const d = new Date();
    return d.getUTCMonth() >= 7 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
  }, []);
  const zone = reglages.donnees?.zone ?? 'B';
  const vacances = useDonnees<{
    zone: string;
    holidays: Array<{ nom: string; debut: string; fin: string }>;
  }>(`/settings/holidays/${anneeRentree}?zone=${zone}`, [anneeRentree, zone]);

  // ── Colonnes : les domaines de la matière, regroupés par nom ──────────────
  const colonnes = useMemo<ColonneSousDomaine[]>(() => {
    const parNom = new Map<string, string[]>();
    for (const d of arbreProg.donnees ?? []) {
      if (matiereActive && d.subject && d.subject !== matiereActive.name) continue;
      const nom = sansPrefixeNiveau(d.name);
      parNom.set(nom, [...(parNom.get(nom) ?? []), d.id]);
    }
    return [
      ...[...parNom].map(([nom, ids]) => ({ id: ids[0] as string | null, nom, ids })),
      // Toujours présente : elle recueille les séances libres et celles dont le
      // domaine a disparu d'une réforme du programme.
      { id: null, nom: 'Général', ids: [] as string[] },
    ];
  }, [arbreProg.donnees, matiereActive]);

  // ── Lignes : les semaines de la période ───────────────────────────────────
  const semainesPerso = lireSemainesPerso(reglages.donnees?.semaines_perso);
  const semaines = useMemo<LigneSemaine[]>(() => {
    if (!periode) return [];
    const perso = semainesPerso?.[periode.id];
    const liste = semainesDePeriode(periode, semainesPerso);
    const conges = vacances.donnees?.holidays ?? [];
    return liste.map((lundi, i) => ({
      lundi,
      vendredi: decalerJour(lundi, 4),
      rang: i + 1,
      /*
       * Semaines choisies À LA MAIN : jamais grisées.
       *
       * L'enseignant qui a composé sa liste l'a fait exprès — lui répondre
       * « vacances » sur une semaine qu'il vient d'ajouter serait absurde.
       */
      vacances: !(perso && perso.length > 0) && semaineEnVacances(lundi, conges),
      courante: lundi <= aujourdhui && aujourdhui <= decalerJour(lundi, 6),
    }));
  }, [periode, semainesPerso, vacances.donnees, aujourdhui]);

  /** Semaines réellement travaillées : c'est ce chiffre qui sert à répartir. */
  const semainesEffectives = semaines.filter((s) => !s.vacances).length;

  /*
   * Le seul écran qui se découpe en feuilles : une matière peut compter quinze
   * sous-domaines, et quinze colonnes sur une page donnent des bandes de
   * quelques millimètres où les mots se coupent lettre à lettre.
   */
  const pdf = useTelechargementPdf();

  const sequences = useMemo<SequenceCellule[]>(
    () => (progressions.donnees ?? [])
      .filter((p) => !matiereActive || p.subjectId === matiereActive.id)
      .map((p) => ({
        id: p.id,
        curriculumDomainId: p.curriculumDomainId,
        weekStart: p.weekStart,
        sequence: p.sequence,
        competencies: p.competencies,
        content: p.content,
        done: p.done,
        reportedFromId: p.reportedFromId,
      })),
    [progressions.donnees, matiereActive],
  );

  /** Ce qui décide de la couleur des croix : la période, plus ses copies parties ailleurs. */
  const pourLesCroix = useMemo<SequenceCellule[]>(
    () => [
      ...sequences,
      ...(reporteesAilleurs.donnees ?? []).map((p) => ({
        id: p.id,
        curriculumDomainId: p.curriculumDomainId,
        weekStart: p.weekStart,
        sequence: p.sequence,
        competencies: p.competencies,
        content: p.content,
        done: p.done,
        reportedFromId: p.reportedFromId,
      })),
    ],
    [sequences, reporteesAilleurs.donnees],
  );

  /*
   * Repli quand aucune matière n'est choisie : la teinte d'ardoise de la
   * palette de données, et non un violet écrit en dur qui ne figurait nulle
   * part ailleurs — il était par ailleurs répété à l'identique dans l'écran de
   * programmation.
   */
  const { theme } = useTheme();
  const couleurTableau = matiereActive?.color
    ?? (arbreProg.donnees ?? [])[0]?.color ?? couleurDonnees(7, theme);
  const pave = paveTeinte(couleurTableau, theme);

  /*
   * Le PDF de la période, au modèle de la v1 (`lib/gabarits-v1.ts`) : la
   * couleur de la matière en en-tête et en bande, découpé en blocs de cinq
   * colonnes au-delà de six sous-domaines.
   */
  const impression = {
    enCours: pdf.enCours,
    erreur: pdf.erreur,
    lancer: () => {
      void pdf.telecharger(demandeV1(progressionsV1(
        titreV1(matiereActive?.name, periode?.name, annee.donnees?.label),
        couleurTableau,
        semaines,
        colonnes,
        sequences,
      )));
    },
  };

  // ── Ajout d'une séance ────────────────────────────────────────────────────
  const [ajout, setAjout] = useState<{ colonne: ColonneSousDomaine; lundi: string } | null>(null);
  const [choisis, setChoisis] = useState<string[]>([]);
  const [libre, setLibre] = useState('');

  /**
   * Contenus proposés : les blocs de la PROGRAMMATION de cette période.
   *
   * C'est tout l'emboîtement du produit : la programmation cadre la période, la
   * progression la découpe en semaines. Retaper les intitulés à la main est
   * exactement le travail que l'application doit éviter — et c'est ainsi qu'on
   * s'écarte de ce qu'on avait prévu sans s'en apercevoir.
   */
  const blocsProposes = useMemo(() => {
    if (!ajout) return [];
    const retenus = new Set(ajout.colonne.ids);
    return (blocs.donnees ?? []).filter((b) =>
      b.termId === periodeId
      && (!matiereActive || b.subjectId === matiereActive.id)
      && b.content.trim() !== ''
      // Ligne « Général » : tout ce qui n'est rattaché à aucun domaine.
      && (ajout.colonne.id === null
        ? !b.curriculumDomainId
        : !!b.curriculumDomainId && retenus.has(b.curriculumDomainId)));
  }, [ajout, blocs.donnees, periodeId, matiereActive]);

  function ouvrirAjout(colonne: ColonneSousDomaine, lundi: string) {
    setAjout({ colonne, lundi });
    setChoisis([]);
    setLibre('');
  }

  /** Chaque élément coché donne UNE séance ; le texte reste modifiable ensuite. */
  async function creerSeances() {
    if (!ajout) return;
    const textes = [...choisis, libre.trim()].map((t) => t.trim()).filter(Boolean);
    if (textes.length === 0) {
      setResume('Choisissez un contenu ou saisissez une séance libre.');
      return;
    }
    const ok = await executer(async () => {
      for (const [i, texte] of textes.entries()) {
        await api.requete('/progressions', {
          methode: 'POST',
          corps: {
            weekStart: ajout.lundi,
            termId: periodeId,
            ...(matiereActive ? { subjectId: matiereActive.id } : {}),
            // Le nom permet au serveur de matérialiser la matière d'une école
            // neuve, qui n'a encore aucune ligne `subjects`.
            subjectName: matiereActive?.name,
            curriculumDomainId: ajout.colonne.id,
            sequence: texte,
            position: sequences.length + i,
          },
        });
      }
    });
    if (ok) {
      setAjout(null);
      await Promise.all([progressions.recharger(), matieres.recharger()]);
    }
  }

  // ── Modifications ─────────────────────────────────────────────────────────
  /*
   * ── Écritures d'une même séance : dans l'ordre, et sur le dernier état ──
   * Les détails d'une séance sont les LIGNES d'un même champ. Modifier la
   * ligne 1, puis aussitôt la ligne 2, envoyait la seconde écriture construite
   * sur l'affichage, qui ne connaissait pas encore la première : elle
   * remettait l'ancienne ligne 1 et effaçait la saisie.
   *
   * `competencesEnCours` garde le dernier texte ENVOYÉ tant qu'une écriture
   * est en route, et chaque modification part de lui. Les écritures d'une
   * même séance s'enchaînent, pour arriver au serveur dans l'ordre de frappe.
   */
  const fileEcritures = useRef(new Map<string, Promise<boolean>>());
  const competencesEnCours = useRef(new Map<string, { texte: string; enRoute: number }>());

  function ecrire(id: string, corps: Record<string, unknown>): Promise<boolean> {
    const precedente = fileEcritures.current.get(id) ?? Promise.resolve(true);
    const suivante = precedente.then(async () => {
      try {
        await api.requete(`/progressions/${id}`, { methode: 'PATCH', corps });
        await progressions.recharger();
        return true;
      } catch { return false; }
    });
    fileEcritures.current.set(id, suivante);
    void suivante.finally(() => {
      if (fileEcritures.current.get(id) === suivante) fileEcritures.current.delete(id);
    });
    return suivante;
  }

  /** Les lignes de détail d'une séance, écritures en route comprises. */
  const lignesDe = (s: SequenceCellule) =>
    enSousBulles(competencesEnCours.current.get(s.id)?.texte ?? s.competencies);

  async function ecrireLignes(s: SequenceCellule, lignes: string[]): Promise<boolean> {
    const texte = lignes.join('\n');
    const enCours = competencesEnCours.current.get(s.id);
    competencesEnCours.current.set(s.id, { texte, enRoute: (enCours?.enRoute ?? 0) + 1 });
    try {
      return await ecrire(s.id, { competencies: texte });
    } finally {
      const etat = competencesEnCours.current.get(s.id);
      if (etat && --etat.enRoute === 0) competencesEnCours.current.delete(s.id);
    }
  }

  const modifierTitre = (s: SequenceCellule, texte: string) => ecrire(s.id, { sequence: texte });

  /** Une sous-bulle vide reste une LIGNE : sinon la numérotation se décale. */
  function modifierBulle(s: SequenceCellule, index: number, texte: string): Promise<boolean> {
    const lignes = lignesDe(s);
    lignes[index] = texte.replace(/\n/g, ' ') || ' ';
    return ecrireLignes(s, lignes);
  }

  async function ajouterBulle(s: SequenceCellule) {
    // Une espace, et non la chaîne vide : la ligne doit exister même sans texte,
    // sinon le champ créé disparaît au rechargement.
    await ecrireLignes(s, [...lignesDe(s), ' ']);
  }

  async function supprimerBulle(s: SequenceCellule, index: number) {
    const lignes = lignesDe(s);
    lignes.splice(index, 1);
    await ecrireLignes(s, lignes);
  }

  async function supprimerSeance(s: SequenceCellule) {
    const ok = await confirmer({
      titre: 'Supprimer cette séance ?',
      message: s.sequence || s.content || 'Séance sans intitulé.',
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    });
    if (!ok) return;
    if (await executer(() => api.requete(`/progressions/${s.id}`, { methode: 'DELETE' }))) {
      notifier('Séance supprimée.');
      await progressions.recharger();
    }
  }

  // ── Report ────────────────────────────────────────────────────────────────
  async function envoyerReport(s: SequenceCellule, lignes: number[], titreSeul: boolean) {
    const ok = await executer(async () => {
      const r = await api.requete<{
        reported: boolean; canceled: boolean; lines: number; week: string | null; reason: string | null;
      }>(`/progressions/${s.id}/report`, {
        methode: 'POST',
        corps: { lineIndexes: lignes, titleOnly: titreSeul },
      });
      /*
       * Un report qui franchit les vacances atterrit dans la période suivante :
       * la copie quitte l'écran, et il faut dire où elle est partie — sinon on
       * la croit perdue et on la ressaisit.
       */
      const derniere = semaines[semaines.length - 1]?.lundi ?? '9999-12-31';
      if (r.reason) setResume(r.reason);
      else if (r.canceled) setResume('Report annulé.');
      else if (r.reported && r.week && r.week > derniere) {
        const jour = new Date(`${r.week}T00:00:00Z`).toLocaleDateString('fr-FR', {
          day: 'numeric', month: 'long', timeZone: 'UTC',
        });
        setResume(`Reporté à la semaine du ${jour}, dans la période suivante.`);
      } else if (r.reported) setResume('Reporté à la semaine suivante.');
    });
    if (ok) await Promise.all([progressions.recharger(), reporteesAilleurs.recharger()]);
  }

  /** Croix du titre : toute la séance, ou l'annulation si tout l'était déjà. */
  function reporterSeance(s: SequenceCellule) {
    const tout = seanceToutReportee(s, pourLesCroix);
    // Les détails ÉCRITS : un détail vide n'a rien à reporter.
    const ecrites = enSousBulles(s.competencies)
      .flatMap((t, i) => (t.trim() ? [i] : []));
    // Tout était reporté → on annule en n'envoyant plus rien. Sans détail
    // écrit, c'est le titre seul qui part : envoyer des lignes vides valait
    // annulation, et la croix ne faisait rien.
    void envoyerReport(s, tout ? [] : ecrites, !tout && ecrites.length === 0);
  }

  /** Croix d'un détail : le titre et ce détail seulement. */
  function reporterBulle(s: SequenceCellule, index: number) {
    // Un détail vide ne part pas dans la copie : l'envoyer seul valait
    // annulation, et défaisait le report du titre sans prévenir.
    if (!(enSousBulles(s.competencies)[index] ?? '').trim()) {
      setResume('Écrivez ce détail avant de le reporter.');
      return;
    }
    const rep = bullesReportees(s, pourLesCroix);
    const set = new Set(rep.set);
    if (set.has(index)) set.delete(index); else set.add(index);
    void envoyerReport(s, [...set].sort((a, b) => a - b), false);
  }

  /**
   * Récupération : recopie sur la première semaine tout ce qui est resté non
   * traité dans les périodes ANTÉRIEURES.
   *
   * Les originales ne sont pas touchées : elles restent en place comme trace de
   * ce qui était prévu. C'est un ajout, jamais un déplacement — sinon
   * l'historique de la période écoulée se réécrirait après coup.
   */
  async function rattraper() {
    const cible = semaines.find((s) => s.courante) ?? semaines[0];
    if (!periode || !cible) return;
    const ok = await executer(async () => {
      const r = await api.requete<{ recovered: number; reason: string | null }>(
        '/progressions/recover',
        { methode: 'POST', corps: { termId: periode.id, targetWeek: cible.lundi } },
      );
      setResume(
        r.reason ? r.reason
          : r.recovered > 0
            ? `${r.recovered} séance(s) non traitée(s) recopiée(s) sur la semaine ${cible.rang}.`
            : 'Rien à rattraper : tout était traité.',
      );
    });
    if (ok) {
      setRattrapage(false);
      await progressions.recharger();
    }
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if (annee.erreur) {
    return (
      <Carte titre="Progressions">
        <Vide titre="Aucune année scolaire active">
          Les progressions se rattachent aux périodes de l'année en cours.
          Créez une année scolaire dans les réglages pour commencer.
        </Vide>
      </Carte>
    );
  }

  if (progressions.erreur) return <Message ton="erreur">{progressions.erreur}</Message>;

  return (
    <div className="flex flex-col gap-4">
      {impression.erreur && <Message ton="erreur">{impression.erreur}</Message>}
      {erreurEcriture && <Message ton="erreur">{erreurEcriture}</Message>}
      {resume && <Message ton="succes">{resume}</Message>}

      <EnTetePage
        titre="Progressions"
        contexte={periode
          ? `${periode.name} · ${semainesEffectives} semaine(s) effective(s)`
          : 'Aucune période'}
        actions={
          <Bouton
            variante="ghost"
            icone={<IconeTelecharger taille={16} />}
            chargement={impression.enCours}
            onClick={impression.lancer}
          >
            Télécharger le PDF
          </Bouton>
        }
      />

      {(periodes.donnees?.length ?? 0) > 0 && (
        <BarreOutils>
          {/* Onglets de période : c'est l'unité de préparation. Naviguer de
              semaine en semaine pour atteindre P4 demandait quinze clics, en
              traversant les vacances. */}
          <GroupeBoutons
            legende="Période"
            valeur={periodeId}
            surChangement={(id) => setParametres({ periode: id })}
            options={(periodes.donnees ?? []).map((p) => ({
              valeur: p.id, texte: p.name,
            }))}
          />
          {(matieres.donnees?.length ?? 0) > 1 && (
            <Selecteur
              libelle="Matière"
              value={matiereActive?.id ?? ''}
              onChange={(e) => setMatiereFiltre(e.target.value)}
              options={(matieres.donnees ?? []).map((m) => ({ valeur: m.id, texte: m.name }))}
            />
          )}
          <Bouton variante="secondary" onClick={() => setRattrapage(true)}>
            Récupération
          </Bouton>
        </BarreOutils>
      )}

      <Carte>
        {(periodes.donnees?.length ?? 0) === 0 ? (
          <Vide titre="Aucune période définie">
            Les périodes suivent le calendrier officiel et se créent avec l’année
            scolaire. Si elles manquent, prévenez la direction de l’école ou
            écrivez au support.
          </Vide>
        ) : progressions.chargement && !progressions.donnees ? (
          <SqueletteTableau lignes={6} colonnes={4} />
        ) : semaines.length === 0 ? (
          <Vide titre="Aucune semaine dans cette période">
            Les semaines suivent le calendrier officiel. Si elles vous semblent
            fausses, prévenez la direction de l’école ou écrivez au support.
          </Vide>
        ) : (
          <div className="flex flex-col gap-3">
            <p
              className="rounded-r-lg px-3 py-1.5 text-[13px]"
              /*
               * ── La couleur de la matière ne s'écrit pas telle quelle ──────
               * Le nom était posé dans la teinte brute sur un fond à 7 % de
               * cette même teinte : mesuré à 1,4:1 en thème sombre — le mot se
               * devinait plus qu'il ne se lisait. Le pavé dérivé donne un fond
               * et une encre faits l'un pour l'autre, dans les deux thèmes.
               */
              style={{
                borderLeft: `4px solid ${pave.filet}`,
                background: pave.fond,
                color: pave.encre,
              }}
            >
              <span className="font-bold">
                {matiereActive?.name ?? 'Toutes les matières'}
              </span>
              {/*
                * La période et le nombre de semaines ne sont PAS répétés ici.
                *
                * L'en-tête de page les annonce déjà, quatre-vingts pixels plus
                * haut : « Progressions — Période 1 · 6 semaine(s) effective(s) »
                * puis « Français — Période 1 · 6 sem. effectives ». Une
                * information affichée deux fois à cette distance est une
                * information qu'on cesse de lire, et deux lignes de moins avant
                * la première semaine du tableau.
                */}
            </p>

            <TableauProgressions
              semaines={semaines}
              sousDomaines={colonnes}
              sequences={sequences}
              pourLesCroix={pourLesCroix}
              couleur={couleurTableau}
              surAjoutSeance={(colonne, lundi) => ouvrirAjout(colonne, lundi)}
              surModificationTitre={modifierTitre}
              surModificationBulle={modifierBulle}
              surAjoutBulle={(s) => void ajouterBulle(s)}
              surSuppressionBulle={(s, i) => void supprimerBulle(s, i)}
              surSuppression={(s) => void supprimerSeance(s)}
              surReportSeance={reporterSeance}
              surReportBulle={reporterBulle}
            />

            <p className="text-[12px] text-[var(--color-fg-muted)]">
              Les modifications sont enregistrées automatiquement. La croix rouge
              reporte à la semaine suivante ; recliquer annule.
            </p>
          </div>
        )}
      </Carte>

      {/* ── Ajout d'une séance ── */}
      <Dialogue
        titre={ajout ? `Séance · ${ajout.colonne.nom}` : 'Séance'}
        ouvert={ajout !== null}
        surFermeture={() => setAjout(null)}
      >
        <div className="flex min-h-0 flex-col gap-4">
          {blocsProposes.length > 0 ? (
            <fieldset className="flex min-h-0 flex-col gap-1">
              <legend className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--color-fg-muted)]">
                Notions et objectifs (Programmation)
              </legend>
              <div className={`flex max-h-56 flex-col gap-1 ${classeListeDialogue}`}>
                {blocsProposes.map((b) => (
                  <label
                    key={b.id}
                    className="flex cursor-pointer items-start gap-2 rounded-md border border-[var(--color-border)] px-2 py-1.5 text-[13px]"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 shrink-0"
                      checked={choisis.includes(b.content)}
                      onChange={(e) => setChoisis((liste) => (e.target.checked
                        ? [...liste, b.content]
                        : liste.filter((t) => t !== b.content)))}
                    />
                    {/*
                      * Le contenu ENTIER, sur autant de lignes qu'il faut : il
                      * restait sur une seule, débordant hors de la fenêtre
                      * avec une barre de défilement, dès qu'un mot était long.
                      */}
                    <span className="min-w-0 flex-1 whitespace-pre-wrap [overflow-wrap:anywhere]">{b.content}</span>
                    {b.source === 'programme' && <Etiquette>Programme</Etiquette>}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : (
            <Message ton="info">
              Aucun contenu dans la programmation de cette période pour «
              {` ${ajout?.colonne.nom ?? ''} `}». Ajoutez-en dans l'écran
              Programmation, ou saisissez une séance libre ci-dessous.
            </Message>
          )}

          <ZoneTexte
            libelle="Ou séance libre"
            value={libre}
            onChange={(e) => setLibre(e.target.value)}
            aide="Chaque élément coché crée une séance. Le texte reste modifiable ensuite."
          />

          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setAjout(null)}>Annuler</Bouton>
            <Bouton
              chargement={enCours}
              onClick={() => void creerSeances()}
              disabled={choisis.length === 0 && libre.trim() === ''}
            >
              Ajouter
            </Bouton>
          </ActionsDialogue>
        </div>
      </Dialogue>

      {/* ── Récupération ── */}
      <Dialogue
        titre="Récupération"
        ouvert={rattrapage}
        surFermeture={() => setRattrapage(false)}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm">
            Recopier les séances <strong>non traitées</strong> des périodes
            précédentes sur la semaine
            {` S${(semaines.find((s) => s.courante) ?? semaines[0])?.rang ?? 1} `}
            de {periode?.name ?? 'cette période'} ?
          </p>
          <Message ton="info">
            Les séances d'origine restent en place : c'est une copie, pas un
            déplacement. L'historique des périodes écoulées n'est pas réécrit.
          </Message>
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={() => setRattrapage(false)}>Annuler</Bouton>
            <Bouton chargement={enCours} onClick={() => void rattraper()}>Récupérer</Bouton>
          </ActionsDialogue>
        </div>
      </Dialogue>
    </div>
  );
}
